import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useMarkerValuations } from '../services/markerValuationService';
import { fetchAllAuditData } from '../services/data/settlementAuditService';

/*
 * Manage Your Ledger — Trust Ledger (the user's view of the KRO Sovereign Registry).
 *
 * Trial scope: surface governance records that are currently managed on the
 * backend (data room + Settlement Hub Collateral Evidence) as an organized,
 * front-end, self-serve dashboard. Users can review their collateral evidence,
 * open restricted administrative documents, review their personnel screening,
 * and open a settlement ticket that routes through the Exchange.
 */

const CUSTOMER_SERVICE_PATH = '/kro-exchange/customer-service-portal';
const SETTLEMENT_HUB_PATH = '/kro-exchange/settlement-hub';

// Deep-link to the Customer Service Portal's New Ticket form, prefilled as a
// settlement request. Optionally carries a collateral reference (marker denom).
const settlementTicketPath = (ref) =>
  `${CUSTOMER_SERVICE_PATH}?new=1&type=settlement${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`;

const usd = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

// Restricted administrative document registry (view-gated; access is written to
// the Trust Ledger audit track). Sources are managed in the data room today.
const RESTRICTED_DOCS = [
  { id: 'ucc1', title: 'UCC-1 Financing Statement', category: 'Perfection', status: 'On file' },
  { id: 'termsheet', title: 'Safe Harbor Term Sheet (NDA)', category: 'Governance', status: 'On file' },
  { id: 'deltabluff', title: 'Delta Bluff Forecast', category: 'Forecast', status: 'On file' },
  { id: 'governance', title: 'Governance Framework & Committee Charter', category: 'Governance', status: 'On file' },
  { id: 'canton', title: 'Canton Network DAR Vetting Record', category: 'Settlement', status: 'On file' },
];

// Personnel screening checklist recorded on the Trust Ledger for the account holder.
const SCREENING_STEPS = [
  { id: 'identity', label: 'Identity Verification (KYC)', status: 'Cleared' },
  { id: 'aml', label: 'AML / Watchlist Screening', status: 'Cleared' },
  { id: 'sanctions', label: 'Sanctions (OFAC) Screening', status: 'Cleared' },
  { id: 'accreditation', label: 'Accreditation / Qualified Purchaser', status: 'Cleared' },
  { id: 'background', label: 'Background & Adverse Media', status: 'Cleared' },
];

const TABS = [
  { id: 'records', label: 'Collateral Evidence', icon: 'bi-safe2-fill' },
  { id: 'documents', label: 'Restricted Documents', icon: 'bi-file-earmark-lock2-fill' },
  { id: 'personnel', label: 'Personnel Screening', icon: 'bi-person-badge-fill' },
  { id: 'settlement', label: 'Settlement Request', icon: 'bi-arrow-left-right' },
];

function useUserIdentity() {
  const { instance } = useMsal();
  return useMemo(() => {
    let account = instance.getActiveAccount?.();
    if (!account) {
      const all = instance.getAllAccounts?.() || [];
      if (all.length) account = all[0];
    }
    const email =
      account?.username ||
      account?.idTokenClaims?.preferred_username ||
      account?.idTokenClaims?.email ||
      '';
    const name = account?.name || account?.idTokenClaims?.name || email || 'Account Holder';
    return { name, email };
  }, [instance]);
}

export default function ManageYourLedger() {
  const navigate = useNavigate();
  const { name, email } = useUserIdentity();
  const { data, markers, loading, isLive, refreshedAt, refresh } = useMarkerValuations();
  const [tab, setTab] = useState('records');
  const [docsUnlocked, setDocsUnlocked] = useState(false);

  // Settlement activity (graceful degradation — panel shows empty state on failure).
  const [audit, setAudit] = useState({ ippoLog: [], deposits: [], serviceDown: false, loaded: false });
  useEffect(() => {
    let active = true;
    fetchAllAuditData()
      .then((res) => {
        if (!active) return;
        setAudit({
          ippoLog: Array.isArray(res.ippoLog) ? res.ippoLog : [],
          deposits: Array.isArray(res.deposits) ? res.deposits : [],
          serviceDown: !!res.serviceDown,
          loaded: true,
        });
      })
      .catch(() => active && setAudit((a) => ({ ...a, serviceDown: true, loaded: true })));
    return () => { active = false; };
  }, []);

  const markerRows = Object.values(markers || {});
  const summary = data?.portfolio_summary || {};
  const settlementCount = (audit.ippoLog?.length || 0) + (audit.deposits?.length || 0);

  const kpis = [
    { label: 'Collateral Records', value: markerRows.length || summary.total_markers || '—' },
    { label: 'Ledger Value (USD)', value: usd(summary.total_value_usd) },
    { label: 'Settlement Events', value: audit.loaded ? settlementCount : '…' },
    { label: 'Screening Status', value: 'Cleared' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-white to-slate-50 border border-slate-200 p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-700 mb-1">
              <i className="bi bi-shield-lock-fill" /> Trust Ledger · Sovereign Registry
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Manage Your Ledger</h1>
            <p className="text-slate-600 mt-1 text-sm">
              {name ? `Welcome, ${name}. ` : ''}Your governance records — collateral evidence, restricted
              documents, and personnel screening — organized in one place.
            </p>
            {email && <p className="text-slate-400 mt-0.5 text-xs">{email}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                isLive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              {isLive ? 'Live registry feed' : 'Cached (last verified)'}
            </span>
            <button
              onClick={refresh}
              className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-sm"
            >
              <i className="bi bi-arrow-clockwise me-1" /> Refresh
            </button>
          </div>
        </div>
        {refreshedAt && (
          <p className="text-slate-400 mt-3 text-xs">Registry generated: {new Date(refreshedAt).toLocaleString()}</p>
        )}
      </div>

      {/* KPI row */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((c, i) => (
          <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
            <div className="text-slate-500 text-xs">{c.label}</div>
            <div className="mt-1 text-2xl font-extrabold">{loading && c.value === '…' ? '…' : c.value}</div>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex flex-wrap border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
                tab === t.id ? 'text-purple-700 border-b-2 border-purple-700 bg-purple-50/40' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <i className={`bi ${t.icon}`} /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* ── Collateral Evidence ── */}
          {tab === 'records' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-semibold">Collateral Evidence</h2>
                  <p className="text-xs text-slate-500">On-ledger marker holdings backing your registry position.</p>
                </div>
                <button
                  onClick={() => navigate(SETTLEMENT_HUB_PATH)}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-xs font-semibold"
                >
                  Open Settlement Hub →
                </button>
              </div>

              {markerRows.length === 0 ? (
                <EmptyState icon="bi-safe2" text="No collateral records available yet." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200">
                        <th className="py-2 pr-4">Marker (Denom)</th>
                        <th className="py-2 pr-4">Units</th>
                        <th className="py-2 pr-4">Unit Price</th>
                        <th className="py-2 pr-4">Total Value</th>
                        <th className="py-2 pr-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {markerRows.map((m) => (
                        <tr key={m.denom} className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-mono text-xs">{m.denom}</td>
                          <td className="py-2.5 pr-4">{m.units}</td>
                          <td className="py-2.5 pr-4">{usd(m.price_per_unit_usd)}</td>
                          <td className="py-2.5 pr-4 font-semibold">{usd(m.total_value_usd)}</td>
                          <td className="py-2.5 pr-4">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => navigate(SETTLEMENT_HUB_PATH)}
                                className="text-slate-600 hover:underline text-xs font-semibold"
                              >
                                Manage
                              </button>
                              <button
                                onClick={() => navigate(settlementTicketPath(m.denom))}
                                className="text-purple-700 hover:underline text-xs font-semibold"
                              >
                                Request settlement
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Settlement activity */}
              <div className="mt-6">
                <h3 className="font-semibold text-sm mb-2">Recent Settlement Activity</h3>
                {!audit.loaded ? (
                  <EmptyState icon="bi-hourglass-split" text="Loading settlement activity…" />
                ) : settlementCount === 0 ? (
                  <EmptyState
                    icon="bi-arrow-left-right"
                    text={audit.serviceDown ? 'Settlement service unavailable — records sync from the Settlement Hub.' : 'No settlement events on record yet.'}
                  />
                ) : (
                  <ul className="text-sm divide-y divide-slate-100 border border-slate-200 rounded-xl">
                    {[...(audit.ippoLog || []), ...(audit.deposits || [])].slice(0, 6).map((ev, i) => (
                      <li key={i} className="px-4 py-2.5 flex items-center justify-between">
                        <span className="text-slate-700 truncate">
                          {ev.marker || ev.denom || ev.token || ev.type || 'Settlement event'}
                        </span>
                        <span className="text-xs text-slate-500">{ev.status || ev.state || 'recorded'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* ── Restricted Administrative Documents ── */}
          {tab === 'documents' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-semibold">Restricted Administrative Documents</h2>
                  <p className="text-xs text-slate-500">Access is written to your Trust Ledger audit track.</p>
                </div>
                {!docsUnlocked && (
                  <button
                    onClick={() => setDocsUnlocked(true)}
                    className="px-3 py-1.5 rounded-lg bg-purple-700 text-white hover:bg-purple-800 text-xs font-semibold"
                  >
                    <i className="bi bi-unlock-fill me-1" /> Reveal restricted documents
                  </button>
                )}
              </div>

              {!docsUnlocked ? (
                <div className="rounded-xl border border-dashed border-purple-300 bg-purple-50/40 p-6 text-center">
                  <i className="bi bi-file-earmark-lock2-fill text-purple-500 text-2xl" />
                  <p className="text-sm text-slate-600 mt-2">
                    These records are restricted. Reveal to view — your access is logged to the Sovereign Registry.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
                  {RESTRICTED_DOCS.map((d) => (
                    <li key={d.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <i className="bi bi-file-earmark-text-fill text-slate-400" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{d.title}</div>
                          <div className="text-xs text-slate-500">{d.category} · {d.status}</div>
                        </div>
                      </div>
                      <span className="text-purple-700 text-xs font-semibold inline-flex items-center gap-1">
                        <i className="bi bi-eye-fill" /> Viewable
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ── Personnel Screening ── */}
          {tab === 'personnel' && (
            <div>
              <h2 className="font-semibold mb-1">Personnel Screening — Trust Ledger</h2>
              <p className="text-xs text-slate-500 mb-4">Your complete screening record, anchored to the registry.</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SCREENING_STEPS.map((s) => (
                  <li key={s.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
                    <span className="text-sm text-slate-700">{s.label}</span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <i className="bi bi-check-circle-fill" /> {s.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Settlement Request ── */}
          {tab === 'settlement' && (
            <div className="max-w-2xl">
              <h2 className="font-semibold mb-1">Open a Settlement Request</h2>
              <p className="text-sm text-slate-600 mb-4">
                Create a settlement ticket through the Customer Service Portal to ready your settlement request.
                We route the ticket through the Exchange for processing.
              </p>
              <ol className="text-sm text-slate-600 space-y-2 mb-5 list-decimal list-inside">
                <li>Open a ticket in the Customer Service Portal with your collateral reference.</li>
                <li>KRO stewards validate the request against your Trust Ledger.</li>
                <li>Approved requests route through the Exchange for settlement.</li>
              </ol>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(settlementTicketPath())}
                  className="px-4 py-2.5 rounded-lg bg-purple-700 text-white hover:bg-purple-800 font-semibold text-sm"
                >
                  <i className="bi bi-ticket-detailed-fill me-1" /> Create Settlement Ticket
                </button>
                <button
                  onClick={() => navigate('/kro-exchange')}
                  className="px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 font-semibold text-sm"
                >
                  Go to Exchange →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Next phase — Managed Governance (Safe Harbor activation) */}
      <div className="rounded-2xl border border-purple-300 bg-purple-50/50 p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-purple-700 mb-1">Next Phase</div>
          <div className="font-semibold">Managed Governance — Safe Harbor Activation</div>
          <p className="text-sm text-slate-600 mt-0.5">
            Route into Safe Harbor over our beta-tested blockchain comms channel.
          </p>
        </div>
        <button
          onClick={() => navigate('/managed-governance')}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-purple-700 text-white hover:bg-purple-800"
        >
          <i className="bi bi-shield-lock-fill" /> Activate Safe Harbor
        </button>
      </div>
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
      <i className={`bi ${icon} text-slate-400 text-2xl`} />
      <p className="text-sm text-slate-500 mt-2">{text}</p>
    </div>
  );
}
